/* ==========================================================================
   Conquest — global.js
   Shared utilities, analytics bus, header behaviour, dialogs, disclosures.
   No dependencies. Loaded with defer on every page.
   ========================================================================== */
(() => {
  const Conquest = (window.Conquest = window.Conquest || {});
  Conquest.settings = Conquest.settings || {};
  Conquest.strings = Conquest.strings || {};

  /* ---------- Translation text ----------
     Shopify's `t` filter HTML-escapes every translation whose key doesn't end in _html
     ("We'd" arrives as "We&#39;d"). Strings serialised for JavaScript are decoded once, here,
     back to plain text — and are only ever inserted with textContent, never as markup.
     The <textarea> is never attached, and its content is parsed as text (RCDATA), so nothing runs. */
  const decoder = document.createElement('textarea');
  Conquest.decodeText = function decodeText(value) {
    if (typeof value === 'string') {
      if (value.indexOf('&') === -1) return value;
      decoder.innerHTML = value;
      return decoder.value;
    }
    if (Array.isArray(value)) return value.map(decodeText);
    if (value && typeof value === 'object') {
      Object.keys(value).forEach((key) => { value[key] = decodeText(value[key]); });
    }
    return value;
  };
  Conquest.decodeText(Conquest.strings);

  /* ---------- Money ---------- */
  Conquest.formatMoney = function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || Conquest.settings.moneyFormat || '£{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const toFixed = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };
    const match = fmt.match(placeholder);
    if (!match) return String(cents);
    let value;
    switch (match[1]) {
      case 'amount_no_decimals': value = toFixed(cents, 0); break;
      case 'amount_with_comma_separator': value = toFixed(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = toFixed(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = toFixed(cents, 2, "'", '.'); break;
      default: value = toFixed(cents, 2);
    }
    return fmt.replace(placeholder, value);
  };

  /* Pence-aware small money, e.g. 64p rather than £0.64 for GBP cost-per-cup. */
  Conquest.formatSmallMoney = function formatSmallMoney(cents) {
    const fmt = Conquest.settings.moneyFormat || '';
    if (cents < 100 && /£/.test(fmt)) return `${Math.round(cents)}p`;
    return Conquest.formatMoney(Math.round(cents));
  };

  /* ---------- Small helpers ---------- */
  Conquest.debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
  };

  Conquest.fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  });

  Conquest.announce = (message) => {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };

  Conquest.template = (str, vars = {}) => String(str || '')
    .replace(/\{\{\s*(\w+)\s*\}\}|\[(\w+)\]/g, (match, a, b) => {
      const key = a || b;
      return key in vars ? vars[key] : match;
    });

  /* ---------- Pub/sub ---------- */
  const subscribers = {};
  Conquest.subscribe = (event, cb) => {
    (subscribers[event] = subscribers[event] || []).push(cb);
    return () => { subscribers[event] = subscribers[event].filter((s) => s !== cb); };
  };
  Conquest.publish = (event, data) => { (subscribers[event] || []).forEach((cb) => cb(data)); };

  /* ---------- Analytics bus ----------
     One place to emit commerce events. Publishes to Shopify customer events
     (usable by custom pixels), optionally dataLayer, and a DOM event.
     Never include personal data here. */
  Conquest.track = (name, detail = {}) => {
    const payload = { event: name, ...detail };
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(`conquest:${name}`, detail);
      }
    } catch (e) { /* analytics must never break the page */ }
    if (Conquest.settings.dataLayer) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    }
    document.dispatchEvent(new CustomEvent('conquest:track', { detail: payload }));
  };

  /* Declarative tracking: <a data-track="wholesale_cta" data-track-label="home"> */
  document.addEventListener('click', (event) => {
    const el = event.target.closest('[data-track]');
    if (el) Conquest.track(el.dataset.track, { label: el.dataset.trackLabel || null });
  });

  /* ---------- Dialog helpers ----------
     Native <dialog> gives us focus containment, Escape handling and inert background. */
  Conquest.openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.body.classList.add('has-dialog');
    const focusTarget = dialog.querySelector('[autofocus]') || dialog.querySelector('[data-dialog-focus]');
    if (focusTarget) focusTarget.focus();
  };

  Conquest.closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  const onDialogClose = (dialog) => {
    if (!document.querySelector('dialog[open]')) document.body.classList.remove('has-dialog');
    if (dialog._opener && typeof dialog._opener.focus === 'function') dialog._opener.focus();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog) {
        event.preventDefault();
        Conquest.openDialog(dialog, opener);
      }
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      Conquest.closeDialog(closer.closest('dialog'));
      return;
    }
    /* Click on the backdrop closes */
    if (event.target.tagName === 'DIALOG' && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) Conquest.closeDialog(event.target);
    }
  });

  document.addEventListener('close', (event) => {
    if (event.target.tagName === 'DIALOG') onDialogClose(event.target);
  }, true);

  /* ---------- Header ---------- */
  class HeaderBar extends HTMLElement {
    connectedCallback() {
      this.sentinel = document.createElement('div');
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;pointer-events:none;';
      document.body.prepend(this.sentinel);
      this.observer = new IntersectionObserver(([entry]) => {
        document.documentElement.classList.toggle('is-scrolled', !entry.isIntersecting);
      }, { rootMargin: '48px 0px 0px 0px' });
      this.observer.observe(this.sentinel);

      /* Desktop dropdowns: close on outside click, Escape, and when another opens */
      this.menus = Array.from(this.querySelectorAll('details[data-mega]'));
      this.menus.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) this.menus.filter((d) => d !== details).forEach((d) => d.removeAttribute('open'));
          const summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        });
        details.addEventListener('keyup', (e) => {
          if (e.key === 'Escape' && details.open) {
            details.removeAttribute('open');
            details.querySelector('summary').focus();
          }
        });
        details.addEventListener('focusout', (e) => {
          if (!details.contains(e.relatedTarget)) details.removeAttribute('open');
        });
      });
      document.addEventListener('click', (e) => {
        this.menus.forEach((d) => { if (d.open && !d.contains(e.target)) d.removeAttribute('open'); });
      });
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.sentinel) this.sentinel.remove();
    }
  }
  if (!customElements.get('header-bar')) customElements.define('header-bar', HeaderBar);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button[name]').forEach((btn) => btn.addEventListener('click', (e) => {
        e.preventDefault();
        const previous = this.input.value;
        if (btn.name === 'plus') this.input.stepUp(); else this.input.stepDown();
        if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateButtons();
      }));
      this.input.addEventListener('change', () => this.updateButtons());
      this.updateButtons();
    }
    updateButtons() {
      const value = parseInt(this.input.value, 10);
      const min = parseInt(this.input.min || '0', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = max !== null && value >= max;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Newsletter tracking (Shopify customer form) ---------- */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('[data-newsletter-form]')) Conquest.track('newsletter_signup', { location: form.dataset.location || null });
    if (form.matches('[data-wholesale-form]')) Conquest.track('wholesale_enquiry', { business_type: (form.querySelector('[name="contact[Business type]"]') || {}).value || null });
  });

  /* ---------- Deferred video (click to play, no autoplay with sound) ---------- */
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('[data-deferred-play]');
      if (button) button.addEventListener('click', () => this.load());
    }
    load() {
      const template = this.querySelector('template');
      if (!template || this.loaded) return;
      this.loaded = true;
      const content = template.content.firstElementChild.cloneNode(true);
      this.appendChild(content);
      this.classList.add('is-loaded');
      const video = content.matches('video') ? content : content.querySelector('video');
      if (video) video.play();
    }
  }
  if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);

  /* Scroll story (coffee-process section): swap the pinned image as each step reaches the middle */
  class ProcessStory extends HTMLElement {
    connectedCallback() {
      const steps = Array.from(this.querySelectorAll('[data-process-step]'));
      const layers = Array.from(this.querySelectorAll('[data-process-layer]'));
      const progress = this.querySelector('[data-process-progress]');
      if (!steps.length || !('IntersectionObserver' in window)) return;
      const activate = (i) => {
        steps.forEach((s, n) => s.classList.toggle('is-active', n === i));
        layers.forEach((l, n) => l.classList.toggle('is-active', n === i));
        if (progress) progress.style.transform = `scaleY(${(i + 1) / steps.length})`;
      };
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) activate(steps.indexOf(e.target)); });
      }, { rootMargin: '-45% 0px -45% 0px' });
      steps.forEach((s) => this.observer.observe(s));
    }
    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
  }
  if (!customElements.get('process-story')) customElements.define('process-story', ProcessStory);

  /* Pause-able ambient video (muted loops respect reduced motion) */
  document.querySelectorAll('video[data-ambient]').forEach((video) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.removeAttribute('autoplay');
      video.pause();
    }
  });
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-video-toggle]');
    if (!toggle) return;
    const video = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!video) return;
    if (video.paused) { video.play(); toggle.setAttribute('aria-pressed', 'false'); }
    else { video.pause(); toggle.setAttribute('aria-pressed', 'true'); }
  });

  /* ---------- Cookie preferences ----------
     Shopify's cookie banner exposes window.privacyBanner once it has loaded. The footer link
     stays hidden unless that API is present, so it never becomes a dead link. */
  const revealCookiePreferences = () => {
    const banner = window.privacyBanner;
    if (!banner || typeof banner.showPreferences !== 'function') return;
    document.querySelectorAll('[data-cookie-preferences][hidden]').forEach((item) => { item.hidden = false; });
  };
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-cookie-preferences] button')) return;
    if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') window.privacyBanner.showPreferences();
  });
  revealCookiePreferences();
  window.addEventListener('load', () => { revealCookiePreferences(); setTimeout(revealCookiePreferences, 1500); });
})();
